import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CircleNotch, MagnifyingGlass } from '@phosphor-icons/react';
import { useIcd10Search } from './icd10/useIcd10Search';
import SelectedCodeDisplay from './icd10/SelectedCodeDisplay';
import ResultsDropdown from './icd10/ResultsDropdown';
import BillableBadge from './icd10/BillableBadge';

/**
 * Reusable ICD-10 diagnosis code search component.
 * Typeahead search with billable indicator.
 *
 * Props:
 *   value        - current ICD-10 code string
 *   description  - current description string (optional, for display)
 *   onChange     - (code, description, billable) => void
 *   label        - field label text (default: "Diagnosis Code (ICD-10)")
 *   placeholder  - input placeholder
 *   required     - show asterisk
 *   testId       - data-testid attribute
 */
const ICD10Search = ({
  value = '',
  description = '',
  onChange,
  label = 'Diagnosis Code (ICD-10)',
  placeholder = 'Search by code or description...',
  required = false,
  testId = 'icd10-search',
}) => {
  const {
    wrapperRef,
    query,
    results,
    searching,
    open,
    handleInputChange,
    handleSelect,
    handleClear,
  } = useIcd10Search({ onChange });

  const showNoResults = open && query.length >= 2 && results.length === 0 && !searching;
  const showResults = open && results.length > 0;

  return (
    <div className="space-y-2" ref={wrapperRef}>
      {label && (
        <Label>
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}

      {value ? (
        <SelectedCodeDisplay
          value={value}
          description={description}
          onClear={handleClear}
          testId={testId}
        />
      ) : (
        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="pl-9 font-mono"
            data-testid={testId}
          />
          {searching && (
            <CircleNotch
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400"
            />
          )}
        </div>
      )}

      {showResults && (
        <ResultsDropdown results={results} onSelect={handleSelect} testId={testId} />
      )}

      {showNoResults && (
        <div className="border rounded-lg p-3 bg-white shadow-lg text-sm text-slate-500 text-center">
          No ICD-10 codes found for "{query}"
        </div>
      )}
    </div>
  );
};

export { BillableBadge };
export default ICD10Search;
